import { BRAND_INFO, CHANNEL_LABEL, CONTENT_TYPE_LABEL, STATUS_LABEL } from '@hub/shared'
import type { BrandId, Channel, ContentType, Status } from '@hub/shared'

/** Brand colours via CSS variables (index.css), so dark mode gets its own, accessible values. */
export const brandFill = (b: BrandId): string => `var(--brand-${b})`
export const brandText = (b: BrandId): string => `var(--brand-${b}-text)`
export const brandTint = (b: BrandId, percent = 10): string => `color-mix(in srgb, var(--brand-${b}) ${percent}%, transparent)`
/**
 * A selected chip in the brand colour: stronger tint plus a ring in the full
 * brand colour. Keeps the orange vivid (a solid fill would need a darker,
 * browner orange to carry white text at 4.5:1) and, with the check mark the
 * callers add, never relies on colour alone.
 */
export const brandSelected = (b: BrandId): React.CSSProperties => ({
  backgroundColor: brandTint(b, 18),
  color: brandText(b),
  boxShadow: `inset 0 0 0 2px ${brandFill(b)}`,
})

export function BrandDot({ brand, size = 8 }: { brand: BrandId; size?: number }): JSX.Element {
  return (
    <span
      title={BRAND_INFO[brand].name}
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, backgroundColor: brandFill(brand) }}
    />
  )
}

/** Brand name in its colour, on a faint tint of it. */
export function BrandTag({ brand }: { brand: BrandId }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-caption font-semibold"
      style={{ color: brandText(brand), backgroundColor: brandTint(brand) }}
    >
      <BrandDot brand={brand} size={6} />
      {BRAND_INFO[brand].name}
    </span>
  )
}

export const typeLabel = (t: ContentType): string => CONTENT_TYPE_LABEL[t]
export const channelLabel = (c: Channel): string => CHANNEL_LABEL[c]
export const statusLabel = (s: Status): string => STATUS_LABEL[s]

/** Status colours: neutral while it is only an idea, warm while it is being worked on, green once out. */
export const STATUS_TONE: Record<Status, string> = {
  idee: '#AEAEB2',
  geplant: '#8E8E93',
  in_arbeit: '#FF9500',
  freigabe: '#AF52DE',
  eingeplant: '#007AFF',
  veroeffentlicht: '#34C759',
  verworfen: '#C7C7CC',
}
